import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AudioProvider } from "./contexts/AudioContext";
import { AdminProvider } from "./contexts/AdminContext";
import { UserProvider } from "./contexts/UserContext";
import Navigation from "./components/Navigation";
import AudioPlayerBar from "./components/AudioPlayerBar";
import AdminGate from "./components/AdminGate";
import AssetManager from "./components/AssetManager";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import SeriesDetail from "./pages/SeriesDetail";
import Episodes from "./pages/Episodes";
import EpisodeDetail from "./pages/EpisodeDetail";
import MusicPage from "./pages/MusicPage";
import SeriesBible from "./pages/SeriesBible";
import WritingAssistant from "./pages/WritingAssistant";
import ProductionHub from "./pages/ProductionHub";
import HelpPage from "./pages/HelpPage";
import StorePage from "./pages/StorePage";
import PaymentSuccess from "./pages/PaymentSuccess";
import CharacterEditor from "./pages/CharacterEditor";
import AuthCallback from "./pages/AuthCallback";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import EmailVerificationBanner from "./components/EmailVerificationBanner";
import WitnessStand from "./pages/WitnessStand";
import WitnessStandLibrary from "./pages/WitnessStandLibrary";
import WitnessStandWrite from "./pages/WitnessStandWrite";
import WitnessStandStory from "./pages/WitnessStandStory";
import WitnessStandMyStories from "./pages/WitnessStandMyStories";
import WitnessStandModerate from "./pages/WitnessStandModerate";
import Diagnostic from "./pages/Diagnostic";
import FileRecovery from "./pages/FileRecovery";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Auto-seed production database on first visit
function AutoSeed() {
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL + "/api";
    const doSeed = async () => {
      try {
        const statsRes = await fetch(`${API}/seed/status`);
        if (!statsRes.ok) return;
        const status = await statsRes.json();
        console.log("[AutoSeed] DB status:", status);
        if (status.is_empty) {
          // /api/seed is now admin-protected. Only trigger if the visitor has an
          // admin token (i.e. the studio owner). Otherwise, surface a manual prompt
          // via Home.jsx's admin-only banner.
          const adminToken = localStorage.getItem("admin_token") || "";
          if (!adminToken) {
            console.log("[AutoSeed] Empty database — admin must initialize via Home banner.");
            return;
          }
          console.log("[AutoSeed] Empty database — triggering admin seed...");
          const seedRes = await fetch(`${API}/seed`, {
            method: "POST",
            headers: { "X-Admin-Token": adminToken },
            credentials: "include",
          });
          if (!seedRes.ok) return;
          const seedResult = await seedRes.json();
          console.log("[AutoSeed] Seed result:", seedResult);
          if (seedResult.seeded) {
            console.log("[AutoSeed] Database seeded — reloading in 1s...");
            setTimeout(() => window.location.reload(), 1000);
          }
        }
      } catch (e) {
        console.error("[AutoSeed] Error:", e);
      }
    };
    doSeed();
  }, []);
  return null;
}

function AppRouter() {
  const location = useLocation();
  // Check URL fragment for session_id synchronously during render (prevents race conditions)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollToTop />
      <AutoSeed />
      <Navigation />
      <EmailVerificationBanner />
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Home />} />
        <Route path="/series/:slug" element={<SeriesDetail />} />
        <Route path="/series/:slug/episodes" element={<Episodes />} />
        <Route path="/episodes" element={<Episodes />} />
        <Route path="/episodes/:id" element={<EpisodeDetail />} />
        <Route path="/music" element={<MusicPage />} />
        <Route path="/store" element={<StorePage />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/manual" element={<HelpPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/bible" element={<SeriesBible />} />
        <Route path="/witness-stand" element={<WitnessStandLibrary />} />
        <Route path="/witness-stand/library" element={<WitnessStandLibrary />} />
        <Route path="/witness-stand/inspiration" element={<WitnessStand />} />
        <Route path="/witness-stand/write" element={<WitnessStandWrite />} />
        <Route path="/witness-stand/write/:storyId" element={<WitnessStandWrite />} />
        <Route path="/witness-stand/story/:storyId" element={<WitnessStandStory />} />
        <Route path="/witness-stand/my" element={<WitnessStandMyStories />} />
        <Route path="/witness-stand/moderate" element={<AdminGate><WitnessStandModerate /></AdminGate>} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/diag" element={<Diagnostic />} />

        {/* Admin-protected routes */}
        <Route path="/production" element={<AdminGate><ProductionHub /></AdminGate>} />
        <Route path="/writing" element={<AdminGate><WritingAssistant /></AdminGate>} />
        <Route path="/assets" element={<AdminGate><AssetManager /></AdminGate>} />
        <Route path="/characters/new" element={<AdminGate><CharacterEditor /></AdminGate>} />
        <Route path="/characters/:id" element={<AdminGate><CharacterEditor /></AdminGate>} />
        <Route path="/admin/recovery" element={<AdminGate><FileRecovery /></AdminGate>} />
      </Routes>
      <Footer />
      <AudioPlayerBar />
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AudioProvider>
        <AdminProvider>
          <UserProvider>
            <BrowserRouter>
              <AppRouter />
            </BrowserRouter>
          </UserProvider>
        </AdminProvider>
      </AudioProvider>
    </ThemeProvider>
  );
}

export default App;
